/** Outcome of reading files out of a drag-and-drop `DataTransfer`. */
export interface DroppedFiles {
  /** Files the browser actually handed over, in drop order. */
  files: File[];
  /** At least one dropped entry was a directory, which we cannot upload. */
  hadDirectory: boolean;
  /** The browser exposed at least one file or directory entry. When false, the drop carried nothing usable. */
  receivedAnything: boolean;
}

/**
 * Read dropped files from a `DataTransfer`, preferring `items` over `files`.
 *
 * `DataTransfer.files` stays empty in several real-world drops — sandboxed browsers
 * (Flatpak/Snap portals) and some file managers expose the payload only through
 * `DataTransfer.items` or as a `text/uri-list`. Reading `items` first recovers those
 * cases; `files` remains the fallback for browsers that populate only it.
 *
 * Directories are reported through `hadDirectory` rather than silently dropped, so the
 * caller can explain why nothing was uploaded.
 */
export function extractDroppedFiles(dataTransfer: DataTransfer): DroppedFiles {
  const files: File[] = [];
  let hadDirectory = false;
  let sawEntry = false;

  // `items` is a live list tied to the drop event, so snapshot it before any await.
  const items = dataTransfer.items ? Array.from(dataTransfer.items) : [];
  for (const item of items) {
    if (item.kind !== 'file') continue;
    sawEntry = true;

    // `webkitGetAsEntry` is the only synchronous way to tell a folder from a file.
    const entry = typeof item.webkitGetAsEntry === 'function' ? item.webkitGetAsEntry() : null;
    if (entry?.isDirectory) {
      hadDirectory = true;
      continue;
    }

    const file = item.getAsFile();
    if (file) files.push(file);
  }

  if (files.length === 0) {
    const fallback = dataTransfer.files ? Array.from(dataTransfer.files) : [];
    if (fallback.length > 0) {
      sawEntry = true;
      files.push(...fallback);
    }
  }

  return { files, hadDirectory, receivedAnything: sawEntry };
}
